import { Injectable } from '@angular/core';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { ModalService } from '@app/core/modal.service';
import { User, ValidationKey } from '@app/models';
import { UserService, ValidationService } from '@app/services';
import {
  ModalPublicProperties,
  ModalResult,
  SetMissingUserInfoModalComponent as ModalComponent,
  userNameValidations,
  userCpfValidations,
  userBirthdayValidations
} from './set-missing-user-info-modal.component';

type BaseModalPublicProperties = ModalPublicProperties & { dismissAsError?: boolean };
type CustomModalPublicProperties = Pick<
  BaseModalPublicProperties,
  | 'ignoreEmailAndPassword'
  | 'ignoreCpf'
  | 'forceBirthdateAs'
  | 'forceCpfAs'
  | 'forceNameAs'
  | 'dismissAsError'
  | 'prefilledFields'
  | 'ignoreLocale'
  | 'ignoreBirthdate'
  | 'forceEdit'
  | 'isSigningFlow'
  | 'documentLocale'
>;

@Injectable({ providedIn: 'root' })
export class SetMissingUserInfoModalService {
  constructor(
    private modalService: ModalService,
    private userService: UserService,
    private validationService: ValidationService
  ) {}

  open(properties?: BaseModalPublicProperties, options?: NgbModalOptions) {
    const dismissAsError = properties.dismissAsError;
    delete properties.dismissAsError;

    return this.modalService.open<ModalResult>(ModalComponent, properties, options, dismissAsError);
  }

  openIfMissingUserInfo(properties?: CustomModalPublicProperties, options?: NgbModalOptions): Observable<ModalResult> {
    return this.userService
      .getCurrentUser({ fetchPolicy: 'cache-first' })
      .pipe(switchMap(currentUser => (this.isMissingUserInfo(currentUser, properties || {}) ? this.open({ currentUser, ...(properties || {}) }, options) : of<ModalResult>(undefined))));
  }

  isMissingUserInfo(user: User, options: CustomModalPublicProperties = {}) {
    const isUserBirthdayInvalid = !!this.validationService.validate(user.birthday, userBirthdayValidations.filter(val => !options.ignoreBirthdate || val !== 'required') as ValidationKey[], {
      appMatchBirthdate: [options.forceBirthdateAs]
    });
    const isUserCpfInvalid = !!this.validationService.validate(user.cpf, userCpfValidations.filter(val => !options.ignoreCpf || val !== 'required') as ValidationKey[], {
      appMatchCpf: [options.forceCpfAs]
    });
    const isUserNameInvalid = !!this.validationService.validate(user.name, userNameValidations, { appMatchName: [options.forceNameAs] });

    return (
      user &&
      (isUserBirthdayInvalid ||
        isUserCpfInvalid ||
        isUserNameInvalid ||
        (!user.birthday && !options.ignoreBirthdate) ||
        (user.email ? !user.email_verified_at : false) ||
        (options.ignoreEmailAndPassword ? false : !user.email) ||
        options.forceEdit)
    );
  }
}
